/**
 * Map a backend error response to an i18n key.
 *
 * Surfacing `body.message` directly is a phishing vector because some
 * message fields are tenant-controlled (organization names, custom email
 * templates) or otherwise free-form. The SPA branches on the stable
 * `error` code and renders a localized string the application controls.
 *
 * Returns `'errors.generic'` if the body has no recognised `error` code.
 */
const KNOWN_CODES: Record<string, string> = {
  token_expired: 'errors.token_expired',
  token_invalid: 'errors.token_invalid',
  email_taken: 'errors.email_taken',
  weak_password: 'errors.weak_password',
  rate_limited: 'errors.rate_limited',
};

export function mapApiError(body: unknown): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const code = (body as { error: unknown }).error;
    if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(KNOWN_CODES, code)) {
      return KNOWN_CODES[code]!;
    }
  }
  return 'errors.generic';
}

/**
 * Structured password-policy violation. The shape matches
 * `sentinel-domain::value_objects::PasswordViolation` serialized with
 * `#[serde(tag = "code", content = "params", rename_all = "snake_case")]`.
 */
export interface PasswordViolation {
  code:
    | 'too_short'
    | 'too_long'
    | 'missing_uppercase'
    | 'missing_lowercase'
    | 'missing_digit'
    | 'missing_special';
  params?: { min?: number; max?: number };
}

export interface WeakPasswordBody {
  error: 'weak_password';
  violations: PasswordViolation[];
}

export function isWeakPassword(body: unknown): body is WeakPasswordBody {
  if (!body || typeof body !== 'object') return false;
  const b = body as { error?: unknown; violations?: unknown };
  if (b.error !== 'weak_password') return false;
  if (!Array.isArray(b.violations)) return false;
  return true;
}

const VIOLATION_KEYS: Record<PasswordViolation['code'], string> = {
  too_short: 'errors.password.too_short',
  too_long: 'errors.password.too_long',
  missing_uppercase: 'errors.password.missing_uppercase',
  missing_lowercase: 'errors.password.missing_lowercase',
  missing_digit: 'errors.password.missing_digit',
  missing_special: 'errors.password.missing_special',
};

/**
 * Translate each `PasswordViolation` into an i18n key and the params the
 * template needs (e.g. `{ min: 8 }` for `errors.password.too_short`).
 *
 * Unknown violation codes (e.g. a future `pwned`) fall back to
 * `errors.password.unknown`. This keeps the SPA forward-compatible with
 * a backend that adds new violation kinds.
 */
export function mapPasswordViolations(
  body: WeakPasswordBody,
): { i18nKey: string; params: Record<string, number> }[] {
  return body.violations.map((v) => {
    const code = v.code as string;
    const i18nKey = Object.prototype.hasOwnProperty.call(VIOLATION_KEYS, code)
      ? VIOLATION_KEYS[v.code]
      : 'errors.password.unknown';
    const params: Record<string, number> = {};
    if (v.params?.min !== undefined) params.min = v.params.min;
    if (v.params?.max !== undefined) params.max = v.params.max;
    return { i18nKey, params };
  });
}
